import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import MotionMedia from "../components/MotionMedia";
import { videoJsonLd } from "../lib/videoSchema";
import { GUIDE_BY_SLUG, STYLE_GUIDES, STYLE_GROUPS } from "../data/curtainStyleGuides";
import { getMedia } from "../data/mediaRegistry";

const SITE = "https://timefurniture.ae";

function Block({ title, children, testid }) {
  return (
    <section data-testid={testid} className="border-t border-[#6E1F2C]/15 pt-8">
      <h2 className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] font-medium mb-4">{title}</h2>
      {children}
    </section>
  );
}

export default function CurtainStyleGuide() {
  const { slug } = useParams();
  const guide = GUIDE_BY_SLUG[slug];

  usePageMeta({
    title: guide ? `${guide.name} — Style Guide | Time Furniture Dubai` : "Curtain Style Guide | Time Furniture",
    description: guide ? `${guide.short} What you see, construction, fitting, benefits and key considerations — measured, stitched and installed in Dubai by Time Furniture.` : "",
    canonical: guide ? `${SITE}/curtains-dubai/styles/${guide.slug}` : `${SITE}/curtains-dubai`,
    ogImage: guide ? `${SITE}${(getMedia(guide.mediaKey) || {}).poster || ""}` : undefined,
    jsonLd: guide
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "Curtains Dubai", item: `${SITE}/curtains-dubai` },
              { "@type": "ListItem", position: 3, name: guide.name, item: `${SITE}/curtains-dubai/styles/${guide.slug}` },
            ],
          },
          videoJsonLd({
            key: guide.mediaKey,
            name: `${guide.name} — Dubai design visualization`,
            description: `${guide.short} Design visualization by Time Furniture, Dubai.`,
            seconds: 10,
          }),
        ].filter(Boolean)
      : undefined,
  });

  if (!guide) return <Navigate to="/curtains-dubai" replace />;

  const group = STYLE_GROUPS.find((g) => g.id === guide.group);
  const related = (guide.related || []).map((s) => GUIDE_BY_SLUG[s]).filter(Boolean);
  const ctaHref = `/curtains-dubai?style=${encodeURIComponent(guide.formValue)}#curtain-quote`;

  return (
    <div data-testid={`style-guide-${guide.slug}`} className="bg-[#FAF5EC]">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Curtains Dubai", to: "/curtains-dubai" },
            { label: guide.name },
          ]}
        />
        <p className="eyebrow mt-4 mb-4">{group ? group.label : "Style Guide"}</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1C110C] max-w-3xl">
          {guide.name.split(" (")[0]}
          {guide.name.includes("(") && (
            <span className="text-[#6E1F2C]"> ({guide.name.split(" (")[1]}</span>
          )}
        </h1>
        <p className="mt-5 max-w-2xl text-[#3E2A22] text-base md:text-lg leading-relaxed">{guide.short}</p>

        <div className="mt-10">
          <figure>
            <MotionMedia
              mediaKey={guide.mediaKey}
              alt={`${guide.name} — Time Furniture Dubai`}
              mode="hero"
              aspectClass="aspect-[16/9]"
              testid={`style-video-${guide.slug}`}
              eager
            />
          </figure>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          <Block title="What You See" testid="guide-what-you-see">
            <p className="text-[#3E2A22] leading-[1.85]">{guide.whatYouSee}</p>
          </Block>
          <Block title={guide.group === "blind" || guide.group === "operation" ? "Construction & System" : "Heading & Stitching"} testid="guide-construction">
            <p className="text-[#3E2A22] leading-[1.85]">{guide.construction}</p>
          </Block>
          <Block title="Fitting & Installation" testid="guide-fitting">
            <p className="text-[#3E2A22] leading-[1.85]">{guide.fitting}</p>
          </Block>
          <Block title="Benefits" testid="guide-benefits">
            <ul className="space-y-2.5">
              {guide.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[#3E2A22]">
                  <Check size={15} className="text-[#A6822F] mt-1 shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </Block>
          <Block title="Who It's For" testid="guide-who-for">
            <p className="text-[#3E2A22] leading-[1.85]">{guide.whoFor}</p>
          </Block>
          <Block title="Key Considerations" testid="guide-considerations">
            <ul className="space-y-2.5">
              {guide.considerations.map((c) => (
                <li key={c} className="flex items-start gap-2.5 text-[#3E2A22]">
                  <span className="text-[#6E1F2C] mt-0.5">—</span> {c}
                </li>
              ))}
            </ul>
          </Block>
        </div>

        <div className="mt-14 bg-white border border-[#6E1F2C]/15 p-7 md:p-9 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <p className="font-serif-display text-2xl md:text-3xl text-[#1C110C]">Like this style?</p>
            <p className="mt-2 text-sm text-[#7A6B5F]">
              We&rsquo;ll carry it into your quote — measurement, stitching and installation by our own team.
            </p>
          </div>
          <Link
            to={ctaHref}
            data-testid="guide-request-style-cta"
            className="shrink-0 inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors"
          >
            Request This Style <ArrowRight size={14} />
          </Link>
        </div>

        {related.length > 0 && (
          <div className="mt-14">
            <p className="eyebrow mb-6">Related Styles</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <div
                  key={r.slug}
                  data-testid={`guide-related-${r.slug}`}
                  className="group bg-white border border-[#6E1F2C]/15 hover:border-[#6E1F2C] transition-colors"
                >
                  {/* Media controls are siblings of the link — no nested interactive elements */}
                  <MotionMedia
                    mediaKey={r.mediaKey}
                    alt={r.name}
                    mode="card"
                    aspectClass="aspect-[4/3]"
                    testid={`related-media-${r.slug}`}
                  />
                  <div className="p-4">
                    <Link
                      to={`/curtains-dubai/styles/${r.slug}`}
                      data-testid={`guide-related-link-${r.slug}`}
                      className="font-serif-display text-lg text-[#1C110C] hover:text-[#6E1F2C] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A963]"
                    >
                      {r.name.split(" (")[0]}
                    </Link>
                    <p className="mt-1 text-xs text-[#7A6B5F] leading-relaxed">{r.short}</p>
                    <Link
                      to={`/curtains-dubai/styles/${r.slug}`}
                      data-testid={`guide-related-explore-${r.slug}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-[#6E1F2C] border-b border-[#D4A963]/60 pb-0.5 hover:text-[#56151F] transition-colors"
                    >
                      Explore style →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12">
          <Link to="/curtains-dubai" className="text-[11px] tracking-[0.24em] uppercase text-[#6E1F2C] border-b border-[#6E1F2C]/40 pb-1 hover:text-[#56151F]" data-testid="guide-back-link">
            ← All curtain &amp; blind styles
          </Link>
        </div>
      </div>
    </div>
  );
}

export { STYLE_GUIDES };
